import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@parrot-software/design-system';
import { CircleAlert } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { CustomerStates } from '@subscriptions/domain/types/SubscriptionsConst';

interface ChurnedSubscriptionsBannerProps {
  blocks: SubscriptionBlockViewDto[];
}

const NAMED_HEALTHY_LIMIT = 2;

/**
 * ChurnedSubscriptionsBanner - Summary of the churned subscriptions of the account.
 *
 * The healthy subscriptions are named while there are at most two of them and counted
 * beyond that, so the sentence stays readable on large accounts. Takes every block of
 * the account, not the searched ones: it describes the account, not the filter.
 */
export const ChurnedSubscriptionsBanner = ({ blocks }: ChurnedSubscriptionsBannerProps) => {
  const { t } = useTranslation('subscriptions');

  const churnedCount = blocks.filter((block) => block.customerState === CustomerStates.INACTIVE).length;
  if (churnedCount === 0) return null;

  const healthy = blocks.filter((block) => block.customerState !== CustomerStates.INACTIVE);

  const getHealthySentence = () => {
    if (healthy.length === 0) return null;
    if (healthy.length === 1) return t('list.churnBanner.healthyOne', { name: healthy[0].name });
    if (healthy.length === NAMED_HEALTHY_LIMIT) {
      return t('list.churnBanner.healthyTwo', { first: healthy[0].name, second: healthy[1].name });
    }
    return t('list.churnBanner.healthyMany', { count: healthy.length });
  };

  const reactivationKey =
    healthy.length > 0 ? 'list.churnBanner.reactivationOthers' : 'list.churnBanner.reactivationAll';

  const details = [getHealthySentence(), t(reactivationKey, { count: churnedCount })].filter(Boolean).join(' ');

  return (
    <Alert
      className={cn(
        'flex items-start gap-3 rounded-lg bg-destructive/10 font-sans',
        '[&>svg]:relative [&>svg]:left-auto [&>svg]:top-auto [&>svg+div]:translate-y-0 [&>svg~*]:pl-0',
      )}
      variant="destructive"
    >
      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <AlertDescription className="whitespace-pre-line text-sm font-medium">
        <strong className="font-bold">{t('list.churnBanner.churned', { count: churnedCount })}</strong> {details}
      </AlertDescription>
    </Alert>
  );
};
